import { loadCompanyCensusEnv } from "@/env";
import { createApiClient } from "@cakewalk/frontend-api";

/**
 * Pre-login client for the company-census ACTIVATION flow.
 *
 * Existing owners with no platform login yet activate here: enter email → verify
 * identity → set password → SMS 2FA. All three calls hit PUBLIC platform-API
 * endpoints (no Cognito session yet), which proxy to the internal db-service:
 *   - /start           — resolve the owner by email, choose a verify method.
 *   - /verify-identity — SSN-last4 or DOB + last name (checked by db-service).
 *   - /complete        — provision the Cognito login (SMS 2FA on first sign-in).
 *
 * The `token` / `verifiedToken` are short-lived, server-signed session tokens
 * that bind the steps together — the browser just carries them between calls.
 */

function baseUrl(): string {
  return loadCompanyCensusEnv().VITE_API_BASE_URL.replace(/\/$/, "");
}

function publicClient() {
  return createApiClient({ baseUrl: baseUrl(), requireAuth: false });
}

export type CensusVerificationMethod = "ssn_last4" | "dob_lastName";

export interface CensusActivationStart {
  token: string;
  verificationMethod: CensusVerificationMethod;
  phoneOnFile: boolean;
  maskedPhone: string | null;
}

/** Resolve the owner by email and learn which identity check to ask for. */
export function startCensusActivation(email: string): Promise<CensusActivationStart> {
  return publicClient().post<CensusActivationStart>("/api/quote-census/activation/start", {
    email: email.trim(),
  });
}

export interface CensusActivationVerifyInput {
  token: string;
  method: CensusVerificationMethod;
  dob?: string;
  lastName?: string;
  ssnLast4?: string;
}

export interface CensusActivationVerifyResult {
  match: boolean;
  attemptsRemaining?: number;
  locked?: boolean;
  verifiedToken?: string;
}

/** Verify identity. A match returns a `verifiedToken` required by complete. */
export function verifyCensusActivationIdentity(
  input: CensusActivationVerifyInput,
): Promise<CensusActivationVerifyResult> {
  return publicClient().post<CensusActivationVerifyResult>(
    "/api/quote-census/activation/verify-identity",
    { ...input },
  );
}

export interface CensusActivationCompleteInput {
  verifiedToken: string;
  password: string;
  /** Only sent when no phone is on file (start `phoneOnFile === false`). */
  phone?: string;
}

export interface CensusActivationCompleteResult {
  success: boolean;
  sub: string;
}

/** Provision the Cognito login once identity is verified. */
export function completeCensusActivation(
  input: CensusActivationCompleteInput,
): Promise<CensusActivationCompleteResult> {
  return publicClient().post<CensusActivationCompleteResult>(
    "/api/quote-census/activation/complete",
    { ...input },
  );
}
